import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Divider, Hidden, Typography } from '@mui/material';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import useImage from 'services/Hooks/useImage';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import { usePatchApplyPromoCode } from 'services/Data/CartService';
import { FileTypeEnumValue } from 'enums/FileTypeEnum';

const useStyles = makeStyles(() => ({
  pageFull: {
    width: '100%',
    marginTop: '1vh',
  },
  noItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const OrderInfoCheckoutSection = ({ cart, isLoading, isDisabled }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { getInventoryItemImage, getPanelTypeImage, getToolingImage } = useImage();

  const { mutate: applyPromoCode } = usePatchApplyPromoCode();

  const totalSavings = cart?.cartItems?.reduce((acc, item) => {
    const fullPriceTotal = item.unitPrice * item.quantity;
    const actualTotal = item.subTotal ?? fullPriceTotal;
    const savings = fullPriceTotal - actualTotal;

    return acc + (savings > 0 ? savings : 0);
  }, 0);

  return (
    <Box className={classes.pageFull}>
      <GridContainer>
        {/* Section for the Cart wall */}
        {cart &&
          cart?.cartWalls?.map(wall => {
            const numberOfPanelImages = wall?.images?.filter(
              i => i.fileType === FileTypeEnumValue.Photo && (!i.numberOfPanels || i.numberOfPanels === wall.numberOfPanels),
            );
            return (
              <React.Fragment key={wall.id}>
                <GridItem xs={12} md={2} key={wall.id}>
                  <Box className={classes.center}>
                    <img
                      src={
                        numberOfPanelImages?.length > 0
                          ? getPanelTypeImage(wall.panelType_Id, numberOfPanelImages[0].id)
                          : '/images/brp-logo.png'
                      }
                      alt="img"
                      style={{ maxWidth: '10vh', maxHeight: '10vh' }}
                    />
                  </Box>
                </GridItem>
                <GridItem xs={12} md={6}>
                  <Typography variant="h2">{wall.name}</Typography>
                  <Typography variant="h3" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                    {`${wall.numberOfPanels}`} {t('panels')}
                  </Typography>
                  <Box sx={{ mt: 4 }}>
                    {wall?.cartWallInventoryItems?.map(cw => (
                      <Typography
                        key={`${cw.quantity}X ${cw.name}`}
                        variant="h6"
                        color="text.secondary"
                        sx={{ mt: 1, mb: 1 }}>
                        {`${cw.quantity}X ${cw.name}`}
                      </Typography>
                    ))}

                    <Hidden mdDown>
                      <Typography variant="h4" color="text.primary" sx={{ mt: 4 }}>
                        {`${t('quantity')}: ${wall.quantity}`}
                      </Typography>
                    </Hidden>

                    <Hidden mdUp>
                      <Divider />
                      <Box
                        xs={12}
                        md={3}
                        sx={{
                          alignSelf: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexDirection: 'row',
                          width: '100%',
                          my: 3,
                        }}>
                        <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                          <Box>
                            <Typography variant="h3">{t('quantity')}</Typography>
                          </Box>
                        </Box>
                        <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                          <Typography variant="h3">{wall.quantity}</Typography>
                        </Box>
                      </Box>

                      <Divider />

                      <Box
                        xs={12}
                        md={3}
                        sx={{
                          alignSelf: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexDirection: 'row',
                          width: '100%',
                          my: 3,
                        }}>
                        <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                          <Box>
                            <Typography variant="h3">{t('price')}</Typography>
                          </Box>
                        </Box>
                        <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                          <Typography variant="h3">
                            ${wall?.subTotal.toFixed(2)} {wall.currencyName}
                          </Typography>
                        </Box>
                      </Box>
                    </Hidden>
                  </Box>
                </GridItem>
                <GridItem xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    }}>
                    <Hidden mdDown>
                      <Typography variant="h1">
                        ${wall?.subTotal.toFixed(2)} {wall.currencyName}
                      </Typography>
                    </Hidden>
                  </Box>
                </GridItem>
                <GridItem xs={12}>
                  <Divider />
                </GridItem>
              </React.Fragment>
            );
          })}

        {/* Section for the Cart tooling */}
        {cart &&
          cart?.cartToolings?.map(tooling => (
            <React.Fragment key={tooling.id}>
              <GridItem xs={12} md={2} key={tooling.id}>
                <Box className={classes.center}>
                  <img
                    src={tooling.imgSrc ? getToolingImage(tooling?.tooling_Id, tooling?.imgSrc.id) : '/images/brp-logo.png'}
                    alt="img"
                    style={{ maxWidth: '10vh', maxHeight: '10vh' }}
                  />
                </Box>
              </GridItem>
              <GridItem xs={12} md={6}>
                <Typography variant="h2">{`${tooling.name}`}</Typography>
                {tooling.additionalToolings?.length > 0 &&
                  tooling.additionalToolings.map(at => (
                    <Typography fontSize={15} color="text.secondary" sx={{ mt: 1, mb: 1 }} key={at.id}>{`${
                      at.quantity
                    } X ${at.name || ''}`}</Typography>
                  ))}

                <Hidden mdDown>
                  <Typography variant="h4" color="text.primary" sx={{ mt: 4 }}>
                    {`${t('quantity')}: ${tooling.quantity}`}
                  </Typography>
                </Hidden>

                <Hidden mdUp>
                  <Divider />
                  <Box
                    xs={12}
                    md={3}
                    sx={{
                      alignSelf: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexDirection: 'row',
                      width: '100%',
                      my: 3,
                    }}>
                    <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                      <Box>
                        <Typography variant="h3">{t('quantity')}</Typography>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                      <Typography variant="h3">{tooling.quantity}</Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box
                    xs={12}
                    md={3}
                    sx={{
                      alignSelf: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexDirection: 'row',
                      width: '100%',
                      my: 3,
                    }}>
                    <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                      <Box>
                        <Typography variant="h3">{t('price')}</Typography>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                      <Typography variant="h3">
                        ${tooling?.subTotal.toFixed(2)} {cart.currencyName}
                      </Typography>
                    </Box>
                  </Box>
                </Hidden>
              </GridItem>

              <GridItem xs={12} md={4} display="flex" justifyContent={'space-between'} alignItems={'center'}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                  }}>
                  <Hidden mdDown>
                    <Typography variant="h1">
                      ${tooling?.subTotal?.toFixed(2)} {cart.currency}
                    </Typography>
                  </Hidden>
                </Box>
              </GridItem>
            </React.Fragment>
          ))}

        {(cart?.cartToolings?.length > 0 || cart?.cartWalls?.length > 0) && (
          <GridItem xs={12}>
            <Divider />
          </GridItem>
        )}
        {/* Section for the Cart items */}
        {cart &&
          cart?.cartItems?.map(item => (
            <>
              <GridItem xs={12} md={2} key={item.id}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                    alignItems: 'center',
                  }}>
                  <img
                    src={
                      item.imgSrc ? getInventoryItemImage(item?.inventoryItem_Id, item?.imgSrc.id) : '/images/brp-logo.png'
                    }
                    alt="img"
                    style={{ maxWidth: '10vh', maxHeight: '10vh' }}
                  />
                </Box>
              </GridItem>
              <GridItem xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="h2">{item?.name}</Typography>
                  <Typography variant="h6" color="text.secondary">
                    {item?.sku}
                  </Typography>

                  <Box>
                    <Hidden mdDown>
                      <Typography variant="h4" color="text.primary" sx={{ mt: 4 }}>
                        {t('quantity')}: {item.quantity}
                      </Typography>
                    </Hidden>

                    <Hidden mdUp>
                      <Divider />
                      <Box
                        xs={12}
                        md={3}
                        sx={{
                          alignSelf: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexDirection: 'row',
                          width: '100%',
                          my: 3,
                        }}>
                        <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                          <Box>
                            <Typography variant="h3">{t('quantity')}</Typography>
                          </Box>
                        </Box>
                        <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                          <Typography variant="h3">{item.quantity}</Typography>
                        </Box>
                      </Box>

                      <Divider />

                      <Box
                        xs={12}
                        md={3}
                        sx={{
                          alignSelf: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexDirection: 'row',
                          width: '100%',
                          my: 3,
                        }}>
                        <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                          <Box>
                            <Typography variant="h3">{t('price')}</Typography>
                          </Box>
                        </Box>
                        <Box>
                          {item.unitPrice * item.quantity !== item.subTotal ? (
                            <>
                              <Typography variant="h2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                ${(item.unitPrice * item.quantity).toFixed(2)} {item.currency}
                              </Typography>
                              <Typography variant="h1" color="error" fontWeight="bold">
                                ${item.subTotal.toFixed(2)} {item.currency}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="h1">
                              ${item.subTotal.toFixed(2)} {item.currency}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Hidden>
                  </Box>
                </Box>
              </GridItem>
              <GridItem xs={12} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                  }}>
                  <Hidden mdDown>
                    {item.unitPrice * item.quantity !== item.subTotal ? (
                      <>
                        <Typography variant="h2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                          ${(item.unitPrice * item.quantity).toFixed(2)} {item.currency}
                        </Typography>
                        <Typography variant="h1" color="error" fontWeight="bold">
                          ${item.subTotal.toFixed(2)} {item.currency}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="h1">
                        ${item.subTotal.toFixed(2)} {item.currency}
                      </Typography>
                    )}
                  </Hidden>
                </Box>
              </GridItem>
              <GridItem xs={12}>
                <Divider />
              </GridItem>
            </>
          ))}
      </GridContainer>

      {!cart && (
        <Box className={classes.noItem}>
          <Typography fontSize={20} color="text.secondary" sx={{ mt: 1, mb: 1 }}>
            {t('NoItemInCart')}
          </Typography>
        </Box>
      )}

      <GridContainer>
        <GridItem xs={12} md={6}></GridItem>
        <SkeletonWrapper
          skeletonProps={{
            width: 210,
            height: 33,
          }}
          isLoading={isLoading}
          component={
            <GridItem xs={12} md={6} sx={{ mt: 18 }}>
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <Box>
                    <Typography variant="h1">{t('subTotal')}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h1">${(cart?.subTotal ?? 0).toFixed(2)}</Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  {totalSavings > 0 && (
                    <>
                      <Box>
                        <Typography color="error" variant="h2">
                          {t('totalSavings')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography color="error" variant="h2">
                          ${totalSavings.toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
                <Box sx={{ my: 2 }}>
                  <CustomInput
                    labelText={t('promoCode')}
                    id="city"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      onBlur: ({ target: { value } }) => {
                        if (cart.promoCode !== value) {
                          applyPromoCode({ ...cart, promoCode: value });
                        }
                      },
                      disabled: !cart || isDisabled,
                      defaultValue: cart?.promoCode ?? '',
                    }}
                  />
                  {cart?.promoCode && cart?.promoCodeDiscount > 0 && (
                    <Alert severity="success">{t('valid_promo_code')}</Alert>
                  )}
                  {cart?.promoCode && cart?.promoCodeDiscount === null && (
                    <Alert severity="error">{t('invalid_promo_code')}</Alert>
                  )}
                </Box>
                {cart?.promoCodeDiscount && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                    <Box>
                      <Typography variant="h2">{t('discount')}</Typography>
                    </Box>
                    <Box>
                      <Typography x variant="h2">
                        ${(cart?.promoCodeDiscount ?? 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {cart?.subTotal - cart?.promoCodeDiscount < 0 && (
                  <Alert severity="warning">
                    {t('checkoutDiscoutnWarning', {
                      discountLeftOver: `$${(cart?.promoCodeDiscount - cart?.subTotal).toFixed(2)}`,
                    })}
                  </Alert>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <Box>
                    <Typography variant="h2">{t('shipping')}</Typography>
                  </Box>
                  <Box>
                    <Typography x variant="h2">
                      {cart?.shippingCost + cart?.freightPODCost
                        ? `$${(cart?.shippingCost + cart?.freightPODCost).toFixed(2)}`
                        : t('toBeConfirmed')}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 4,
                  }}>
                  <Box>
                    <Typography variant="h1">{t('total')}</Typography>
                  </Box>
                  <Box>
                    <Typography x variant="h1">
                      ${(cart?.total ?? 0).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </GridItem>
          }
        />
      </GridContainer>
    </Box>
  );
};

export default OrderInfoCheckoutSection;
